/**
 * CorePageWrapper
 *
 * Phase 6: Wraps core pages in PageActionProvider + PageActionLayout
 * to ensure all pages (core and plugin) inherit the same page action
 * UI layer (toolbar, command palette) and action capabilities.
 *
 * Each core page is wrapped with this component, specifying its pageId
 * and optional entity context. The wrapper automatically provides:
 * - PageActionProvider: declares page context for action filtering
 * - PageActionLayout: renders toolbar/palette UI
 *
 * Usage:
 *   <CorePageWrapper pageId="/workflows" entityType="workflow" entityId={id}>
 *     <WorkflowDetailPage />
 *   </CorePageWrapper>
 */

import React from 'react';
import { PageActionProvider } from './actions/PageActionProvider';
import { PageActionLayout } from './actions/PageActionLayout';

export interface CorePageWrapperProps {
  /** Page identifier (route pattern), e.g. "/workflows", "/settings/plugins/:pluginId" */
  pageId: string;
  /** Entity type on this page, if any */
  entityType?: string;
  /** Entity ID on this page, if any */
  entityId?: string;
  /** Additional action context data */
  extra?: Record<string, unknown>;
  /** The page content to wrap */
  children: React.ReactNode;
  /** Hide the toolbar (some pages do their own control layout) */
  hideToolbar?: boolean;
}

export function CorePageWrapper({
  pageId,
  entityType,
  entityId,
  extra,
  children,
  hideToolbar = false,
}: CorePageWrapperProps) {
  return (
    <PageActionProvider
      pageId={pageId}
      entityType={entityType}
      entityId={entityId}
      extra={extra}
    >
      <PageActionLayout hideToolbar={hideToolbar}>
        {children}
      </PageActionLayout>
    </PageActionProvider>
  );
}
